import { useEffect, useRef, useState } from 'react';
import { FaEllipsisV, FaExchangeAlt, FaSignOutAlt, FaTrash } from 'react-icons/fa';
import { getCurrentUserRole } from '../utils/permissions';

/**
 * Dropdown menu for conversation actions (leave, delete, etc.)
 */
export function ConversationOptionsMenu({ conversation, currentUserId, onLeaveConversation }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Check if user is the owner (owners cannot leave groups)
  const { role: myRole } = getCurrentUserRole(conversation, currentUserId);
  const isOwnerOfGroup = conversation.type === 'group' && myRole === 'owner';

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest('.responder-messenger-conversation-options-menu') &&
        !event.target.closest('.responder-messenger-conversation-options-btn')
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleLeave = async () => {
    if (isProcessing) return;

    const actionText =
      conversation.type === 'group' ? 'leave this group' : 'delete this conversation';

    const confirmed = window.confirm(
      `Are you sure you want to ${actionText}? This action cannot be undone.`,
    );

    if (!confirmed) {
      setIsOpen(false);
      return;
    }

    setIsProcessing(true);
    try {
      await onLeaveConversation();
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to leave conversation:', error);

      // Check if it's the owner permission error
      if (error?.response?.status === 403) {
        alert(
          'Owners cannot leave the group. Please transfer ownership to another member first, then you can leave.',
        );
      } else {
        const errorMsg =
          error?.response?.data?.message || `Failed to ${actionText}. Please try again.`;
        alert(errorMsg);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="responder-messenger-conversation-options">
      <button
        type="button"
        className="responder-messenger-icon-btn responder-messenger-conversation-options-btn"
        aria-label="Conversation options"
        onClick={() => setIsOpen((prev) => !prev)}
        title="Options"
      >
        <FaEllipsisV size={14} />
      </button>

      {isOpen && (
        <div className="responder-messenger-conversation-options-menu">
          {isOwnerOfGroup ? (
            <button
              type="button"
              className="responder-messenger-dropdown-item responder-messenger-dropdown-item--info"
              disabled
              title="Transfer ownership to another member before leaving"
            >
              <FaExchangeAlt size={12} />
              <span>Transfer ownership to leave</span>
            </button>
          ) : (
            <button
              type="button"
              className="responder-messenger-dropdown-item text-danger"
              onClick={handleLeave}
              disabled={isProcessing}
            >
              {conversation.type === 'group' ? (
                <>
                  <FaSignOutAlt size={12} />
                  <span>{isProcessing ? 'Leaving...' : 'Leave Group'}</span>
                </>
              ) : (
                <>
                  <FaTrash size={12} />
                  <span>{isProcessing ? 'Deleting...' : 'Delete Conversation'}</span>
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
